/**
 * Public privacy policy. Both stores require a URL that loads with no session
 * and no app installed — a reviewer opens it in a plain browser — and the mobile
 * app links here from its settings.
 *
 * Every claim below has to stay true of the actual product. If a section starts
 * describing something we no longer do (or stops describing something we
 * started), it's a bug in this page.
 *
 * Russian original; the English version is PrivacyEn.tsx. Keep both in step.
 */
import { LegalDoc, List, Section } from '../LegalDoc';

const UPDATED = '5 августа 2026';

export function PrivacyRu() {
  return (
    <LegalDoc
      title="Политика конфиденциальности"
      updated={UPDATED}
      sibling={{ href: '/terms', label: 'Условия использования' }}
    >
      <Section title="Коротко">
        <p>
          Бункер — мессенджер и голосовая связь для команд площадки cispracs. Мы храним то, без чего
          мессенджер не работает: кто ты и что ты отправил. Мы не продаём данные, не показываем
          рекламу и не следим за тобой в других приложениях и на других сайтах.
        </p>
      </Section>

      <Section title="Какие данные мы обрабатываем">
        <List
          items={[
            ['Аккаунт', 'идентификатор на cispracs, ник, отображаемое имя, аватар, команда и её тег — приходят от платформы при входе.'],
            ['Сообщения и вложения', 'то, что ты пишешь и прикрепляешь: текст, фото, файлы, голосовые сообщения, опросы, реакции.'],
            ['Серверы и каналы', 'участие, роли, права, папки и порядок серверов, отметки прочтения.'],
            ['Голосовая связь', 'кто сейчас в канале и состояние микрофона. Сам звук передаётся в реальном времени и не записывается.'],
            ['Техническое', 'сессии устройств (для списка «Устройства» и выхода), версия приложения, ошибки и обращения через «Сообщить о баге».'],
          ]}
        />
      </Section>

      <Section title="Зачем">
        <p>
          Только чтобы приложение работало: доставлять сообщения, показывать историю и непрочитанное,
          соединять голос, применять права, присылать уведомления и разбирать жалобы. Профилирования
          и рекламы нет.
        </p>
      </Section>

      <Section title="Кто это видит">
        <List
          items={[
            ['Другие участники', 'сообщение видят те, кому оно адресовано: участники канала или личного чата.'],
            ['Модераторы сервера', 'жалоба на сообщение вместе со снимком его текста видна модераторам этого сервера.'],
            ['Модерация площадки', 'критичные жалобы и отчёты о багах уходят модераторам cispracs.'],
            ['Хостинг и хранилище', 'серверы и объектное хранилище, на которых работает Бункер.'],
          ]}
        />
        <p className="mt-3">
          Мы не передаём данные рекламным сетям и аналитическим брокерам, потому что их у нас нет.
        </p>
      </Section>

      <Section title="Сколько храним">
        <p>
          Сообщения и вложения — пока их не удалит автор или владелец сервера. Удалённый сервер
          лежит в корзине 30 дней, потом стирается. Логи модерации хранятся, чтобы решение можно было
          проверить. Удаление аккаунта стирает профиль и отвязывает от него историю — см. «Как удалить аккаунт и данные».
        </p>
      </Section>

      <Section title="Твои права">
        <List
          items={[
            ['Удалить аккаунт', 'в приложении: профиль → «Удалить аккаунт». Подробнее — ниже.'],
            ['Удалить сообщение', 'у себя или у всех — если есть право.'],
            ['Скрыть переписку', 'удаление личного чата скрывает его у тебя и не стирает у собеседника.'],
            ['Заблокировать и пожаловаться', 'на пользователя или сообщение — из карточки профиля и меню сообщения.'],
            ['Выйти с устройства', 'список сессий в настройках профиля.'],
          ]}
        />
      </Section>

      <Section id="delete" title="Как удалить аккаунт и данные">
        <p className="mb-2">
          В мобильном приложении: иконка профиля слева внизу →{' '}
          <b className="font-medium text-ink">«Удалить аккаунт»</b>. Подтверждение спросят один раз,
          отменить нельзя. В веб-версии кнопки пока нет — там пиши на адрес ниже.
        </p>
        <List
          items={[
            ['Удаляется сразу', 'профиль, имя, аватар, описание и статус, друзья и заявки, блокировки, папки, отметки прочтения, настройки уведомлений, подписки на пуши, «ответить позже», участие во всех серверах.'],
            ['Отвязывается', 'связь с аккаунтом cispracs. Повторный вход создаст новый пустой аккаунт Бункера — прежний вернуть нельзя.'],
            ['Завершаются', 'все сессии на всех устройствах.'],
            ['Остаётся', 'отправленные сообщения и файлы — они часть переписки других людей, поэтому не стираются из их чатов, но показываются без имени и аватара. Записи модерации сохраняются, чтобы решение можно было проверить.'],
            ['Свои серверы', 'сервер, где ты единственный участник, удаляется вместе с аккаунтом. Сервер с другими участниками нужно сначала передать другому владельцу или удалить — иначе удаление аккаунта закрыло бы его для всех.'],
          ]}
        />
        <p className="mt-3">
          Нет доступа к приложению? Напиши с адреса, привязанного к аккаунту cispracs, на{' '}
          <a className="text-accent underline" href="mailto:privacy@cispracs.ru">
            privacy@cispracs.ru
          </a>{' '}
          — удалим по запросу.
        </p>
      </Section>

      <Section title="Дети">
        <p>
          Бункер не предназначен для детей младше 13 лет и не собирает их данные осознанно.
        </p>
      </Section>

      <Section title="Разрешения на телефоне">
        <List
          items={[
            ['Микрофон', 'голосовые каналы и голосовые сообщения.'],
            ['Камера и галерея', 'снять или выбрать фото для сообщения, аватара, иконки сервера.'],
            ['Уведомления', 'сообщения и звонки, когда приложение закрыто.'],
          ]}
        />
        <p className="mt-3">
          Каждое запрашивается в момент, когда оно нужно, и приложение работает без них — просто без
          соответствующей возможности.
        </p>
      </Section>

      <Section title="Изменения и связь">
        <p>
          Если политика поменяется, здесь появится новая дата. Вопросы и требования по данным —{' '}
          <a className="text-accent underline" href="mailto:privacy@cispracs.ru">
            privacy@cispracs.ru
          </a>
          .
        </p>
      </Section>
    </LegalDoc>
  );
}
